<div class="inspection-app-configuration-container">
  <app-loader *ngIf="isLoading"></app-loader>

  <div class="configuration-header">
    <h3>Inspection App Configuration</h3>
    <p class="text-muted">
      Configure the data shown in the Inspection Tablet App's home page and Application Details panel.
      Each item supports a fallback list of fields resolved in priority order — the first mapping with a
      non-empty value is used.
    </p>
    <span *ngIf="isDirty" class="text-warning">
      <i class="material-icons" style="vertical-align: middle; font-size: 1rem;">warning</i> Unsaved changes
    </span>
  </div>

  <!-- ============================================================== -->
  <!-- Home Page Columns Section (horizontal arrangement)              -->
  <!-- ============================================================== -->
  <section class="config-section mb-5">
    <h4 class="section-title">Home Page Columns</h4>
    <p class="text-muted">
      Columns shown on the Inspection Tablet App's home page list. Drag to reorder columns or fallback fields.
    </p>

    <!-- Add Column Controls -->
    <div class="configuration-controls mb-3">
      <div class="input-group w-auto">
        <input
          type="text"
          class="form-control"
          placeholder="New column label..."
          [(ngModel)]="homePage.newColumnLabel"
          [disabled]="isLoading"
          (keyup.enter)="addColumn(homePage)"
        />
        <button
          class="btn btn-primary"
          (click)="addColumn(homePage)"
          [disabled]="isLoading || !homePage.newColumnLabel.trim()"
        >
          <i class="material-icons" style="vertical-align: middle; font-size: 1rem;">add</i> Add Column
        </button>
      </div>
    </div>

    <p *ngIf="homePage.columns.length > 0" class="text-muted small mb-2">
      Fallback fields priority appear highest to lowest.
    </p>

    <!-- Horizontal columns list -->
    <div
      cdkDropList
      cdkDropListOrientation="horizontal"
      [cdkDropListData]="homePage.columns"
      (cdkDropListDropped)="dropColumn($event, homePage)"
      class="columns-horizontal"
    >
      <div
        *ngFor="let column of homePage.columns; let colIdx = index"
        cdkDrag
        class="card column-card"
      >
        <!-- Column Header -->
        <div class="card-header d-flex align-items-center gap-2 py-2">
          <div cdkDragHandle title="Drag to reorder columns" style="cursor: move;">
            <i class="material-icons text-muted">drag_indicator</i>
          </div>
          <span class="text-muted fw-bold" style="min-width: 1.5rem;">{{ colIdx + 1 }}.</span>
          <input
            type="text"
            class="form-control form-control-sm align-self-center"
            [(ngModel)]="column.label"
            (ngModelChange)="isDirty = true"
            placeholder="Column label"
            [disabled]="isLoading"
            style="margin: 0; position: relative; top: -1px;"
          />
          <button
            class="btn btn-sm btn-link text-danger p-0 ms-auto"
            (click)="deleteColumn(homePage, colIdx)"
            [disabled]="isLoading"
            title="Remove column"
          >
            <i class="material-icons">delete</i>
          </button>
        </div>

        <!-- Mappings -->
        <div class="card-body py-2">
          <div
            cdkDropList
            [cdkDropListData]="column.mappings"
            (cdkDropListDropped)="dropMapping($event, homePage, colIdx)"
            class="mappings-list mb-2"
          >
            <div
              *ngFor="let mapping of column.mappings; let mapIdx = index"
              cdkDrag
              class="mapping-item d-flex align-items-center gap-2 p-2 rounded mb-1"
            >
              <div cdkDragHandle title="Drag to reorder" style="cursor: move;">
                <i class="material-icons text-muted" style="font-size: 1rem;">drag_indicator</i>
              </div>
              <span class="mapping-info flex-grow-1">
                <strong>{{ getConfiguredFieldLabel(mapping.fieldKey, mapping.source) }}</strong>
                <small class="text-muted d-block">Source: <i>{{ getSourceLabel(mapping.source) }}</i></small>
              </span>
              <button
                class="btn btn-sm btn-link text-danger p-0"
                (click)="removeMapping(homePage, colIdx, mapIdx)"
                [disabled]="isLoading"
                title="Remove mapping"
              >
                <i class="material-icons" style="font-size: 1rem;">remove_circle_outline</i>
              </button>
            </div>
            <div *ngIf="column.mappings.length === 0" class="text-muted fst-italic small ps-1">
              No field mappings — add at least one below.
            </div>
          </div>

          <!-- Add Mapping -->
          <div class="add-mapping-controls">
            <div
              ngbDropdown
              container="body"
              placement="bottom-start top-start bottom-end top-end"
              class="add-mapping-dropdown w-100"
              #homePageDrop="ngbDropdown"
            >
              <button
                type="button"
                class="btn btn-sm btn-outline-primary w-100 d-flex align-items-center justify-content-center"
                ngbDropdownToggle
                [disabled]="isLoading"
              >
                <i class="material-icons me-1" style="font-size: 1rem;">add</i>
                <span>Add fallback field</span>
              </button>
              <div ngbDropdownMenu class="fallback-field-dropdown-menu">
                <ng-container *ngIf="getFieldsForMapping(homePage, colIdx, FieldKeyItemSource.Application).length > 0">
                  <h6 class="dropdown-header">System Application Fields</h6>
                  <button
                    *ngFor="let field of getFieldsForMapping(homePage, colIdx, FieldKeyItemSource.Application)"
                    ngbDropdownItem
                    type="button"
                    [title]="field.label"
                    (click)="addFieldMapping(homePage, colIdx, field); homePageDrop.close()"
                  >{{ field.label }}</button>
                </ng-container>
                <ng-container *ngIf="getFieldsForMapping(homePage, colIdx, FieldKeyItemSource.Inspection).length > 0">
                  <h6 class="dropdown-header">System Inspection Fields</h6>
                  <button
                    *ngFor="let field of getFieldsForMapping(homePage, colIdx, FieldKeyItemSource.Inspection)"
                    ngbDropdownItem
                    type="button"
                    [title]="field.label"
                    (click)="addFieldMapping(homePage, colIdx, field); homePageDrop.close()"
                  >{{ field.label }}</button>
                </ng-container>
                <ng-container *ngIf="getFieldsForMapping(homePage, colIdx, FieldKeyItemSource.InspectionCustomFields).length > 0">
                  <h6 class="dropdown-header">Custom Inspection Fields</h6>
                  <button
                    *ngFor="let field of getFieldsForMapping(homePage, colIdx, FieldKeyItemSource.InspectionCustomFields)"
                    ngbDropdownItem
                    type="button"
                    [title]="getInspectionCustomFieldOptionLabel(field)"
                    (click)="addFieldMapping(homePage, colIdx, field); homePageDrop.close()"
                  >{{ getInspectionCustomFieldOptionLabel(field) }}</button>
                </ng-container>
                <div *ngIf="!hasAnyAvailableFields(homePage, colIdx)" class="text-muted small fst-italic px-3 py-2">
                  No more fields available.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div *ngIf="homePage.columns.length === 0 && !isLoading" class="alert alert-info">
      <i class="material-icons" style="vertical-align: middle;">info</i>
      No columns configured. Enter a label above and click "Add Column" to start.
    </div>
  </section>

  <!-- ============================================================== -->
  <!-- Application Details Section (vertical list of detail items)     -->
  <!-- ============================================================== -->
  <section class="config-section mb-5">
    <h4 class="section-title">Application Details</h4>
    <p class="text-muted">
      Fields shown when selecting a scheduled inspection that was downloaded to the tablet app.
      Each detail item supports a fallback list of fields resolved in priority order. Drag to reorder items
      or fallback fields.
    </p>

    <!-- Add Detail Item Controls -->
    <div class="configuration-controls mb-3">
      <div class="input-group w-auto">
        <input
          type="text"
          class="form-control"
          placeholder="New detail label..."
          [(ngModel)]="appDetails.newColumnLabel"
          [disabled]="isLoading"
          (keyup.enter)="addColumn(appDetails)"
        />
        <button
          class="btn btn-primary"
          (click)="addColumn(appDetails)"
          [disabled]="isLoading || !appDetails.newColumnLabel.trim()"
        >
          <i class="material-icons" style="vertical-align: middle; font-size: 1rem;">add</i> Add Detail
        </button>
      </div>
    </div>

    <p *ngIf="appDetails.columns.length > 0" class="text-muted small mb-2">
      Fallback fields priority appear highest to lowest.
    </p>

    <div
      cdkDropList
      [cdkDropListData]="appDetails.columns"
      (cdkDropListDropped)="dropColumn($event, appDetails)"
    >
      <div
        *ngFor="let column of appDetails.columns; let colIdx = index"
        cdkDrag
        class="card mb-2"
      >
        <!-- Header -->
        <div class="card-header d-flex align-items-center gap-2 py-2">
          <div cdkDragHandle title="Drag to reorder" style="cursor: move;">
            <i class="material-icons text-muted">drag_indicator</i>
          </div>
          <span class="text-muted fw-bold" style="min-width: 1.5rem;">{{ colIdx + 1 }}.</span>
          <input
            type="text"
            class="form-control form-control-sm align-self-center"
            [(ngModel)]="column.label"
            (ngModelChange)="isDirty = true"
            placeholder="Detail label"
            [disabled]="isLoading"
            style="margin: 0; position: relative; top: -1px;"
          />
          <button
            class="btn btn-sm btn-link text-danger p-0 ms-auto"
            (click)="deleteColumn(appDetails, colIdx)"
            [disabled]="isLoading"
            title="Remove detail item"
          >
            <i class="material-icons">delete</i>
          </button>
        </div>

        <!-- Mappings -->
        <div class="card-body py-2">
          <div
            cdkDropList
            [cdkDropListData]="column.mappings"
            (cdkDropListDropped)="dropMapping($event, appDetails, colIdx)"
            class="mappings-list mb-2"
          >
            <div
              *ngFor="let mapping of column.mappings; let mapIdx = index"
              cdkDrag
              class="mapping-item d-flex align-items-center gap-2 p-2 rounded mb-1"
            >
              <div cdkDragHandle title="Drag to reorder" style="cursor: move;">
                <i class="material-icons text-muted" style="font-size: 1rem;">drag_indicator</i>
              </div>
              <span class="mapping-info flex-grow-1">
                <strong>{{ getConfiguredFieldLabel(mapping.fieldKey, mapping.source) }}</strong>
                <small class="text-muted" style="margin-left: 0.5rem;">Source: <i>{{ getSourceLabel(mapping.source) }}</i></small>
              </span>
              <button
                class="btn btn-sm btn-link text-danger p-0"
                (click)="removeMapping(appDetails, colIdx, mapIdx)"
                [disabled]="isLoading"
                title="Remove mapping"
              >
                <i class="material-icons" style="font-size: 1rem;">remove_circle_outline</i>
              </button>
            </div>
            <div *ngIf="column.mappings.length === 0" class="text-muted fst-italic small ps-1">
              No field mappings — add at least one below.
            </div>
          </div>

          <!-- Add Mapping -->
          <div class="add-mapping-controls">
            <div
              ngbDropdown
              container="body"
              placement="bottom-start top-start bottom-end top-end"
              class="add-mapping-dropdown w-100"
              #appDetailsDrop="ngbDropdown"
            >
              <button
                type="button"
                class="btn btn-sm btn-outline-primary w-100 d-flex align-items-center justify-content-center"
                ngbDropdownToggle
                [disabled]="isLoading"
              >
                <i class="material-icons me-1" style="font-size: 1rem;">add</i>
                <span>Add fallback field</span>
              </button>
              <div ngbDropdownMenu class="fallback-field-dropdown-menu">
                <ng-container *ngIf="getFieldsForMapping(appDetails, colIdx, FieldKeyItemSource.Application).length > 0">
                  <h6 class="dropdown-header">System Application Fields</h6>
                  <button
                    *ngFor="let field of getFieldsForMapping(appDetails, colIdx, FieldKeyItemSource.Application)"
                    ngbDropdownItem
                    type="button"
                    [title]="field.label"
                    (click)="addFieldMapping(appDetails, colIdx, field); appDetailsDrop.close()"
                  >{{ field.label }}</button>
                </ng-container>
                <ng-container *ngIf="getFieldsForMapping(appDetails, colIdx, FieldKeyItemSource.Inspection).length > 0">
                  <h6 class="dropdown-header">System Inspection Fields</h6>
                  <button
                    *ngFor="let field of getFieldsForMapping(appDetails, colIdx, FieldKeyItemSource.Inspection)"
                    ngbDropdownItem
                    type="button"
                    [title]="field.label"
                    (click)="addFieldMapping(appDetails, colIdx, field); appDetailsDrop.close()"
                  >{{ field.label }}</button>
                </ng-container>
                <ng-container *ngIf="getFieldsForMapping(appDetails, colIdx, FieldKeyItemSource.InspectionCustomFields).length > 0">
                  <h6 class="dropdown-header">Custom Inspection Fields</h6>
                  <button
                    *ngFor="let field of getFieldsForMapping(appDetails, colIdx, FieldKeyItemSource.InspectionCustomFields)"
                    ngbDropdownItem
                    type="button"
                    [title]="getInspectionCustomFieldOptionLabel(field)"
                    (click)="addFieldMapping(appDetails, colIdx, field); appDetailsDrop.close()"
                  >{{ getInspectionCustomFieldOptionLabel(field) }}</button>
                </ng-container>
                <div *ngIf="!hasAnyAvailableFields(appDetails, colIdx)" class="text-muted small fst-italic px-3 py-2">
                  No more fields available.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div *ngIf="appDetails.columns.length === 0 && !isLoading" class="alert alert-info">
      <i class="material-icons" style="vertical-align: middle;">info</i>
      No application details configured. Enter a label above and click "Add Detail" to start.
    </div>
  </section>

  <!-- Action Buttons -->
  <div class="action-buttons mt-4">
    <button
      class="btn btn-success me-2"
      (click)="save()"
      [disabled]="isLoading || !isDirty"
    >
      <i class="material-icons" style="vertical-align: middle; font-size: 1rem;">save</i> Save
    </button>
    <button
      class="btn btn-secondary me-2"
      (click)="cancel()"
      [disabled]="isLoading"
    >
      <i class="material-icons" style="vertical-align: middle; font-size: 1rem;">close</i> Cancel
    </button>
    <button
      class="btn btn-outline-danger"
      (click)="delete()"
      [disabled]="isLoading || !hasAnyConfiguration()"
    >
      <i class="material-icons" style="vertical-align: middle; font-size: 1rem;">delete</i> Delete Configuration
    </button>
  </div>
</div>

<!-- Delete Column Confirmation Modal -->
<wm-modal-confirm
  #deleteColumnModal
  title="Remove Item"
  commandText="Remove"
  [showButton]="false"
  (accepted)="confirmDeleteColumn()"
>
  Are you sure you want to remove this item?
</wm-modal-confirm>

<!-- Discard Changes Confirmation Modal -->
<wm-modal-confirm
  #cancelModal
  title="Unsaved Changes"
  commandText="Discard Changes"
  [showButton]="false"
  (accepted)="confirmCancel()"
>
  You have unsaved changes. Do you want to discard them?
</wm-modal-confirm>

<!-- Delete Configuration Confirmation Modal -->
<wm-modal-confirm
  #deleteConfigModal
  title="Delete Configuration"
  commandText="Delete"
  [showButton]="false"
  (accepted)="confirmDelete()"
>
  Are you sure you want to delete this configuration? This will clear all configured Home Page columns and Application Details.
</wm-modal-confirm>
